'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * Reports that an <img> failed to load, so a caller can swap to an honest
 * fallback (a brand name, a "no thumbnail" box) instead of a broken-image icon.
 *
 * Why a hook and not `onError` on the element: `eventHandlersByType.image`
 * (onError, onLoad) is in jsx-a11y's default handler list, so every
 * `<img onError>` counts as a non-interactive-element interaction in the
 * accessibility ratchet (scripts/accessibility-gate.mjs). Listening on the
 * element from an effect keeps the markup free of handler props and catches
 * the cached-failure case too: an image that already failed before hydration
 * reports `complete` with `naturalWidth === 0` and never fires `error` again.
 */
export function useImageLoadFailure(src: string | null | undefined): {
  ref: React.RefObject<HTMLImageElement | null>
  failed: boolean
} {
  const ref = useRef<HTMLImageElement | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el || !src) return
    const fail = () => setFailed(true)
    if (el.complete && el.naturalWidth === 0) {
      fail()
      return
    }
    el.addEventListener('error', fail)
    return () => el.removeEventListener('error', fail)
  }, [src])
  return { ref, failed }
}
